// src/pages/RegisterPage.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { registerUser } from "../api/authService";
import {
    getCountries,
    getCitiesByCountry,
    getPostalCodesByCity,
    getAddressesByPostalCode,
} from "../api/clientService";

const AGREEMENT_VERSION = "1.0";

const TERMS_CONTENT = `
User Agreement (GDPR Articles 5, 6, 7, 9, 12, 13, 14, 17, 22)

Article 5 — Personal data shall be processed lawfully, fairly and transparently.
Article 6 — Processing is lawful only when at least one legal basis applies.
Article 7 — Consent must be freely given, specific, informed and unambiguous.
Article 9 — Special categories require explicit consent.
Article 12 — Transparent information about data-subject rights.
Article 13 — Information when data are collected from the subject.
Article 14 — Information when obtained from third parties.
Article 17 — Right to erasure (“right to be forgotten”).
Article 22 — Right not to be subject to automated decision-making.
`;

function RegisterPage() {
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        watch,
    } = useForm();

    const [userType, setUserType] = useState("individual");
    const [loading, setLoading] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [serverError, setServerError] = useState("");

    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [postalCodes, setPostalCodes] = useState([]);
    const [addresses, setAddresses] = useState([]);

    const [countryId, setCountryId] = useState("");
    const [cityId, setCityId] = useState("");
    const [postalCodeId, setPostalCodeId] = useState("");
    const [addressId, setAddressId] = useState("");

    const passwordValue = watch("password");

    const toNullableInt = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : null;
    };

    // ==========================
    // LOAD COUNTRIES
    // ==========================
    useEffect(() => {
        if (userType !== "individual") return;

        (async () => {
            try {
                const data = await getCountries();
                setCountries(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Failed to load countries:", e);
                setServerError("Failed to load countries.");
            }
        })();
    }, [userType]);

    // ==========================
    // RESET CHAIN ON USER TYPE CHANGE
    // ==========================
    useEffect(() => {
        // When switching types, reset individual selectors to avoid stale state
        setCountryId("");
        setCityId("");
        setPostalCodeId("");
        setAddressId("");
        setCities([]);
        setPostalCodes([]);
        setAddresses([]);
        setServerError("");
    }, [userType]);

    // ==========================
    // LOAD CITIES
    // ==========================
    useEffect(() => {
        if (userType !== "individual") return;

        // reset chain when country changes
        setCityId("");
        setPostalCodeId("");
        setAddressId("");
        setPostalCodes([]);
        setAddresses([]);

        if (!countryId) {
            setCities([]);
            return;
        }

        (async () => {
            try {
                const data = await getCitiesByCountry(countryId);
                setCities(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Failed to load cities:", e);
                setCities([]);
                setServerError("Failed to load cities.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType, countryId]);

    // ==========================
    // LOAD POSTAL CODES
    // ==========================
    useEffect(() => {
        if (userType !== "individual") return;

        // reset chain when city changes
        setPostalCodeId("");
        setAddressId("");
        setAddresses([]);

        if (!cityId) {
            setPostalCodes([]);
            return;
        }

        (async () => {
            try {
                const data = await getPostalCodesByCity(cityId);
                setPostalCodes(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Failed to load postal codes:", e);
                setPostalCodes([]);
                setServerError("Failed to load postal codes.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType, cityId]);

    // ==========================
    // LOAD ADDRESSES
    // ==========================
    useEffect(() => {
        if (userType !== "individual") return;

        // reset when postal changes
        setAddressId("");

        if (!postalCodeId) {
            setAddresses([]);
            return;
        }

        (async () => {
            try {
                const data = await getAddressesByPostalCode(postalCodeId);
                setAddresses(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Failed to load addresses:", e);
                setAddresses([]);
                setServerError("Failed to load addresses.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType, postalCodeId]);

    // ==========================
    // SUBMIT
    // ==========================
    const onSubmit = async (form) => {
        if (loading) return; // ✅ prevent double submit
        setServerError("");

        if (!form.agreeToTerms) {
            setServerError("You must accept the terms of the agreement.");
            return;
        }

        if (form.password !== form.confirmPassword) {
            setServerError("Passwords do not match.");
            return;
        }

        setLoading(true);

        try {
            const isIndividual = userType === "individual";

            const payload = {
                username: form.username?.trim(),
                email: form.email?.trim(),
                password: form.password,
                name: form.name?.trim(),
                lastName: form.lastName?.trim(),

                dateOfBirth: isIndividual ? form.dateOfBirth : null,
                addressId: isIndividual ? toNullableInt(addressId) : null,
                phoneNumber: isIndividual ? form.phoneNumber : null,

                companyName: !isIndividual ? form.companyName : null,
                taxId: !isIndividual ? form.taxId : null,
                registrationNumber: !isIndividual ? form.registrationNumber : null,
                contactPerson: !isIndividual ? form.contactPerson : null,
                contactPosition: !isIndividual ? form.contactPosition : null,
                companyPhone: !isIndividual ? form.companyPhone : null,
                manualCity: !isIndividual ? form.manualCity : null,
                manualCountry: !isIndividual ? form.manualCountry : null,
                manualPostalCode: !isIndividual ? form.manualPostalCode : null,
                manualAddress: !isIndividual ? form.manualAddress : null,

                agreeToTerms: true,
                agreementVersion: AGREEMENT_VERSION,
                userType: isIndividual ? 0 : 1,
            };

            await registerUser(payload);

            alert("Registration successful!");
            navigate("/login");
        } catch (err) {
            console.error("Registration error:", err);

            const data = err?.response?.data;

            if (data?.errors) {
                const msg = Object.entries(data.errors)
                    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : String(v)}`)
                    .join("\n");
                setServerError(msg);
            } else if (data?.message) {
                setServerError(data.message);
            } else if (typeof data === "string") {
                setServerError(data);
            } else {
                setServerError("Registration failed.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUserTypeChange = (e) => {
        setUserType(e.target.value);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 py-10">
            <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl p-8">

            <button
                type="button"
                className="underline text-blue-600 mb-4"
                onClick={() => setShowTerms((v) => !v)}
            >
                {showTerms ? "Hide Terms" : "Show Terms (GDPR)"}
            </button>

            {showTerms && (
                <pre className="bg-gray-100 p-4 border rounded text-sm whitespace-pre-wrap max-h-60 overflow-y-auto mb-4">
                    {TERMS_CONTENT}
                </pre>
            )}

            {serverError && (
                <div className="mb-4 p-3 border border-red-400 bg-red-50 text-sm text-red-700 whitespace-pre-line">
                    {serverError}
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" onChange={() => serverError && setServerError("")}>

                {/* User Type */}
                <div>
                    <label className="block text-sm font-medium mb-1">User type</label>
                    <select
                        value={userType}
                        onChange={handleUserTypeChange}
                        className="border p-2 w-full rounded"
                    >
                        <option value="individual">Individual</option>
                        <option value="business">Legal Entity</option>
                    </select>
                </div>

                {/* Common fields */}
                <div>
                    <input
                        {...register("username", { required: "Username is required" })}
                        placeholder="Username"
                        className="border p-2 w-full rounded"
                    />
                </div>

                <div>
                    <input
                        {...register("email", {
                            required: "Email is required",
                            pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email format" },
                        })}
                        placeholder="Email"
                        className="border p-2 w-full rounded"
                    />
                </div>

                <div>
                    <input
                        {...register("password", {
                            required: "Password is required",
                            minLength: { value: 6, message: "Password must be at least 6 characters" },
                        })}
                        placeholder="Password"
                        type="password"
                        className="border p-2 w-full rounded"
                    />
                </div>

                <div>
                    <input
                        {...register("confirmPassword", {
                            required: "Please confirm your password",
                            validate: (value) =>
                                value === passwordValue || "Passwords do not match",
                        })}
                        placeholder="Confirm Password"
                        type="password"
                        className="border p-2 w-full rounded"
                    />
                </div>

                <div>
                    <input
                        {...register("name", { required: "Name is required" })}
                        placeholder="Name"
                        className="border p-2 w-full rounded"
                    />
                </div>

                <div>
                    <input
                        {...register("lastName", { required: "Last name is required" })}
                        placeholder="Last name"
                        className="border p-2 w-full rounded"
                    />
                </div>

                {/* INDIVIDUAL BLOCK */}
                {userType === "individual" && (
                    <>
                        <div>
                            <label className="block text-sm font-medium mb-1">Date of birth</label>
                            <input
                                {...register("dateOfBirth", { required: "Date of birth is required" })}
                                type="date"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Country</label>
                            <select
                                value={countryId}
                                onChange={(e) => setCountryId(e.target.value)}
                                className="border p-2 w-full rounded"
                                required
                            >
                                <option value="">-- Select Country --</option>
                                {countries.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">City</label>
                            <select
                                value={cityId}
                                onChange={(e) => setCityId(e.target.value)}
                                className="border p-2 w-full rounded"
                                required
                                disabled={!countryId}
                            >
                                <option value="">-- Select City --</option>
                                {cities.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Postal Code</label>
                            <select
                                value={postalCodeId}
                                onChange={(e) => setPostalCodeId(e.target.value)}
                                className="border p-2 w-full rounded"
                                required
                                disabled={!cityId}
                            >
                                <option value="">-- Select Postal Code --</option>
                                {postalCodes.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.code ?? p.postalCode ?? p.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Address</label>
                            <select
                                value={addressId}
                                onChange={(e) => setAddressId(e.target.value)}
                                className="border p-2 w-full rounded"
                                required
                                disabled={!postalCodeId}
                            >
                                <option value="">-- Select Address --</option>
                                {addresses.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.street}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <input
                                {...register("phoneNumber", { required: "Phone number is required" })}
                                placeholder="Phone"
                                className="border p-2 w-full rounded"
                            />
                        </div>
                    </>
                )}

                {/* BUSINESS BLOCK */}
                {userType === "business" && (
                    <>
                        <div>
                            <input
                                {...register("companyName", { required: "Company name is required" })}
                                placeholder="Company name"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("registrationNumber", {
                                    required: "Registration number is required",
                                })}
                                placeholder="Registration number"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("taxId", { required: "Tax ID is required" })}
                                placeholder="Tax ID"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("contactPerson", {
                                    required: "Contact person is required",
                                })}
                                placeholder="Contact person"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("contactPosition", {
                                    required: "Position is required",
                                })}
                                placeholder="Position"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("companyPhone", {
                                    required: "Company phone is required",
                                })}
                                placeholder="Company phone"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("manualCountry", {
                                    required: "Country is required",
                                })}
                                placeholder="Country"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("manualCity", {
                                    required: "City is required",
                                })}
                                placeholder="City"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("manualPostalCode", {
                                    required: "Postal code is required",
                                })}
                                placeholder="Postal code"
                                className="border p-2 w-full rounded"
                            />
                        </div>

                        <div>
                            <input
                                {...register("manualAddress", {
                                    required: "Address is required",
                                })}
                                placeholder="Address"
                                className="border p-2 w-full rounded"
                            />
                        </div>
                    </>
                )}

                {/* TERMS */}
                <label className="flex items-center space-x-2">
                    <input type="checkbox" {...register("agreeToTerms")} />
                    <span>I accept the terms (version {AGREEMENT_VERSION})</span>
                </label>

                    {/* SUBMIT */}
                    <button
                        type="submit"
                        className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-60"
                        disabled={loading}
                    >
                        {loading ? "Submitting..." : "Register"}
                    </button>

                </form>

            </div>

        </div>
    );
}

export default RegisterPage;